const SESSION_STORAGE_UTM_NAME: string = 'utmSession'
const SESSION_STORAGE_UTM_VALUE: string = 'true'
const GTM_PARAM_GCLID_NAME: string = 'gclid'
const GTM_PARAM_SOURCE_NAME: string = 'utm_source'
const GTM_PARAM_MEDIUM_NAME: string = 'utm_medium'
const GTM_PARAM_CAMPAIGN_NAME: string = 'utm_campaign'
const GTM_PARAM_PARTNER_NAME: string = 'utm_partner'
const COOKIE_UTM_LIST_NAME: string = 'utm_list'
const COOKIE_UTM_STAMP_NAME: string = 'utm_stamp'
const COOKIE_DAYS: number = 365 * 5
const COOKIE_MAX_LENGTH: number = 4000
const HOSTNAME: string = 'adyen.com'

interface UtmParams {
  source: string
  medium: string
  campaign: string
  partner: string
  gclid: string
}

interface UtmValues {
  source: string
  medium: string
  campaign: string
  partner: string
  date: string // YYYY-MM-DD
}

interface FormattedUtmListItem {
  s: string
  m: string
  c: string
  p: string
  d: string
}

interface UtmStampCookieValue {
  current_utm_source: string
  current_utm_medium: string
  current_utm_campaign: string
  current_utm_partner: string
  initial_utm_source: string
  initial_utm_medium: string
  initial_utm_campaign: string
  initial_utm_partner: string
}

/**
 * Set session storage
 * @param {String} name - Session name
 * @param {String} value - Session value
 */
const setSessionStorage = (name: string, value: string): void => {
  if (import.meta.client) {
    window.sessionStorage.setItem(name, value)
  }
}

/**
 * Get URL params
 * @param {String} url - Page URL
 * @returns {Object} - URL GTM parameters
 */
const getUrlParams = (url: string): UtmParams => {
  const parsedUrl = new URL(url)
  return {
    source: parsedUrl.searchParams.get(GTM_PARAM_SOURCE_NAME) || '',
    medium: parsedUrl.searchParams.get(GTM_PARAM_MEDIUM_NAME) || '',
    campaign: parsedUrl.searchParams.get(GTM_PARAM_CAMPAIGN_NAME) || '',
    partner: parsedUrl.searchParams.get(GTM_PARAM_PARTNER_NAME) || '',
    gclid: parsedUrl.searchParams.get(GTM_PARAM_GCLID_NAME) || ''
  }
}

/**
 * Get UTM values
 * @param {Object} params - URL GTM parameters
 * @param {String} date - YYYY-MM-DD
 * @param {String} referrer - Referrer URL
 * @returns {Object} - UTM values
 */
export const getUtmValues = (params: UtmParams, date: string, referrer: string): UtmValues => {
  const { source, medium, campaign, partner, gclid } = params
  const referrerUrl = referrer ? new URL(referrer).hostname : ''

  switch (true) {
    case !!(source || medium || campaign || partner):
      return { source, medium, campaign, partner, date }
    case !!gclid:
      return { source: 'Google', medium: 'Online Advertising', campaign, partner, date }
    case referrer && !referrerUrl.includes(HOSTNAME):
      return { source: referrerUrl, medium: 'Organic', campaign, partner, date }
    default:
      return { source: '', medium: 'Direct/Organic', campaign, partner, date }
  }
}

/**
 * Format UTM list item
 * @param {Object} params - UTM values
 * @returns {Object} - Object with UTM values
 */
export const formatUtmListItem = ({ source, medium, campaign, partner, date }: UtmValues): FormattedUtmListItem => ({
  s: source,
  m: medium,
  c: campaign,
  p: partner,
  d: date
})

export const useGtmParamsStorage = (): any => {
  const { getCookie, setCookie } = useCookies()
  const route = useRoute()

  /**
   * Sets utm_list cookie value
   * @param {String} name - Cookie name
   * @param {Object} value - Cookie value
   */
  const setUtmListCookie = (name: string, value: UtmValues): void => {
    if (!value) {
      return
    }

    const currentValue = getCookie(name)
    const currentList: FormattedUtmListItem[] = currentValue ? JSON.parse(currentValue) : []
    const newListItem: FormattedUtmListItem = formatUtmListItem(value as any)

    let newValue: FormattedUtmListItem[] = [...currentList, newListItem]

    while (JSON.stringify(newValue).length > COOKIE_MAX_LENGTH) {
      newValue = newValue.slice(1)
    }

    setCookie(name, JSON.stringify(newValue), COOKIE_DAYS, false, true)
  }

  /**
   * Sets utm_stamp cookie value
   * @param {String} name - Cookie name
   * @param {Object} value - Cookie value
   */
  const setUtmStampCookie = (name: string, value: UtmValues): void => {
    if (!value) {
      return
    }

    const currentValue = getCookie(name)
    const currentObject: UtmStampCookieValue | null = currentValue ? JSON.parse(currentValue) : null
    const { source, medium, campaign, partner } = value

    const newValue: UtmStampCookieValue = {
      current_utm_source: source,
      current_utm_medium: medium,
      current_utm_campaign: campaign,
      current_utm_partner: partner,
      initial_utm_source: currentObject ? currentObject.initial_utm_source : source,
      initial_utm_medium: currentObject ? currentObject.initial_utm_medium : medium,
      initial_utm_campaign: currentObject ? currentObject.initial_utm_campaign : campaign,
      initial_utm_partner: currentObject ? currentObject.initial_utm_partner : partner
    }

    setCookie(name, JSON.stringify(newValue), COOKIE_DAYS, false, true)
  }

  const runGtmParamStorage = (): void => {
    if (import.meta.client) {
      const hasUtmInSessionStorage = window.sessionStorage.getItem(SESSION_STORAGE_UTM_NAME)
      const urlParams = getUrlParams(document.location.href)

      if (urlParams.gclid) {
        setSessionStorage(GTM_PARAM_GCLID_NAME, urlParams.gclid)
      }

      if (!hasUtmInSessionStorage) {
        setSessionStorage(SESSION_STORAGE_UTM_NAME, SESSION_STORAGE_UTM_VALUE)
        const value: UtmValues = getUtmValues(urlParams, new Date().toISOString().slice(0, 10), document.referrer)
        setUtmListCookie(COOKIE_UTM_LIST_NAME, value)
        setUtmStampCookie(COOKIE_UTM_STAMP_NAME, value)
      }
    }
  }
  return {
    runGtmParamStorage
  }
}
